/**
 * SABA: Nosto autocomplete for the search drawer.
 *
 * Loaded as a plain (non-defer) script ABOVE snippets/saba-nosto-alpine.liquid, and the
 * registration at the bottom of this file runs whichever arrives first, Alpine or this
 * file. Neither guard is redundant: a dynamically inserted script ignores `defer` and
 * Alpine's cdn build starts itself in a microtask on arrival, so load order is not
 * guaranteed by the tag alone.
 *
 * Only loaded when the search-drawer setting "Nosto autocomplete" is on; when it is off the
 * section renders <predictive-search> and this file is never requested.
 */
(() => {
  const ENDPOINT = 'https://search.nosto.com/v1/graphql';
  const MIN_CHARS = 2;
  const DEBOUNCE_MS = 250;
  const KEYWORD_SIZE = 5;
  const PRODUCT_SIZE = 6;
  const CARD_SECTION = 'saba-nosto-drawer-card';
  /* Bounded so a long typing session cannot hold every card it ever rendered.
     JAG's review of the collection grid found an unbounded card cache holding
     ~640 detached cards and their decoded images after 20 pages, on a theme
     with a known memory problem. 54 = 9 terms' worth of a 6-card panel. */
  const CACHE_LIMIT = 54;

  // Nosto search analytics: fire-and-forget, never allowed to throw into the search path.
  const track = (fn) => { if (typeof window.nostojs !== 'function') return; try { window.nostojs((api) => { try { fn(api); } catch (e) {} }); } catch (e) {} };

  /* The market product filter is inlined rather than passed as a variable, so a
     market the filter does not apply to sends the byte-identical request it sent
     before this existed. `filter` is built from theme settings resolved in Liquid
     (sections/search-drawer.liquid), never from shopper input. */
  const queryFor = (filter) => `query SabaAutocomplete($accountId: String!, $q: String!, $kw: Int!, $ps: Int!) {
    search(accountId: $accountId, query: $q, products: { size: $ps${filter} }, keywords: { size: $kw }) {
      redirect
      products { total size from fuzzy hits { productId name url price listPrice imageUrl availability } }
      keywords { hits { keyword _redirect } }
    }
  }`;

  const QUERY = queryFor('');

  const marketClause = (field, values) => {
    if (!field || !values.length) return '';
    return ', filter: [{ field: ' + JSON.stringify(field) +
      ', value: [' + values.map((value) => JSON.stringify(value)).join(', ') + '] }]';
  };

  /* Nosto returns absolute urls on the store's suffixed myshopify domain. */
  const toPath = (url) => {
    if (!url) return '';
    try {
      const parsed = new URL(url, window.location.origin);
      return parsed.pathname + parsed.search;
    } catch (e) {
      return url;
    }
  };

  /* Locale-aware, the way the theme's own PredictiveSearch builds its search url. */
  const root = () => (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  const handleOf = (url) => {
    const match = toPath(url).match(/\/products\/([^/?#]+)/);
    return match ? match[1] : '';
  };

  // Handle of a successful quick add started in `container`; the quick-buy modal moves to <body>, so it is traced via its opener.
  const addedHandle = (event, container) => {
    const form = event.target;
    if (!form || !form.closest) return null;
    const card = form.closest('product-card[handle]');
    if (card) return container.contains(card) ? card.getAttribute('handle') : null;
    const modal = form.closest('quick-buy-modal');
    if (!modal || !(modal.controls || []).some((control) => container.contains(control))) return null;
    return handleOf(modal.getAttribute('product-url'));
  };

  /* Nosto returns keywords lowercased ("dharma column dress"). The drawn row is
     Chronicle Display 18/120 SENTENCE case and --saba-text-transform-heading
     resolves to `none`, so CSS cannot do this; only the first character is
     touched, so a brand or product name keeps its own casing. */
  const sentenceCase = (value) => {
    const text = String(value || '');
    return text ? text.charAt(0).toUpperCase() + text.slice(1) : text;
  };

  /* ------------------------------------------------- card cache and wiring */

  const cardCache = new Map();
  const cacheOrder = [];

  const cachePut = (handle, entry) => {
    if (!cardCache.has(handle)) cacheOrder.push(handle);
    cardCache.set(handle, entry);
    while (cacheOrder.length > CACHE_LIMIT) {
      const evicted = cacheOrder.shift();
      cardCache.delete(evicted);
    }
  };

  /* Re-parsed on read so each injection gets its own nodes: the same handle can
     appear in two consecutive terms and a node can only live in one place. */
  const cardFromCache = (handle) => {
    const entry = cardCache.get(handle);
    if (!entry) return null;
    const doc = new DOMParser().parseFromString('<div>' + entry.card + entry.extras.join('') + '</div>', 'text/html');
    const card = doc.querySelector('.product-card');
    if (!card || !card.getAttribute('handle')) return null;
    const extras = [...doc.querySelectorAll('body > div > *')].filter((el) => el !== card);
    return { card: card, extras: extras };
  };

  /*
   * product-card.liquid renders TWO things per product: the card, and - outside
   * it, so it never sits inside the card's own stacking context - the per-card
   * "Save to wishlist" drawer. Dropping the sibling leaves
   * snippets/saba-card-wishlist-trigger.liquid calling .show() on a drawer that
   * was never injected, which silently does nothing. Same fix as p1-grid's
   * assets/saba-nosto-search.js (commit 8adc043d): park the extras on one host
   * at the end of <body>, which is where a fixed-position drawer wants to be.
   */
  const extrasHost = () => {
    let host = document.getElementById('saba-nosto-card-extras');
    if (!host) {
      host = document.createElement('div');
      host.id = 'saba-nosto-card-extras';
      document.body.appendChild(host);
    }
    return host;
  };

  const injectExtras = (extras) => {
    const host = extrasHost();
    for (const element of extras) {
      const id = element.getAttribute && element.getAttribute('id');
      // drawer ids carry the product id, so re-rendering a product must not
      // stamp a duplicate id into the document
      if (id && document.getElementById(id)) continue;
      host.appendChild(element);
    }
  };

  /*
   * A <script> inside fetched markup NEVER executes - not via innerHTML, not by
   * appending a DOMParser node (custom elements DO still upgrade, so
   * <product-card> itself is fine). product-card.liquid registers its
   * event-delegated variant bar (the plus-button quick add) and its carousel
   * scroll indicator from inline scripts guarded by window.__sabaVariantBarInit
   * and __sabaProductCardScrollIndicatorInit, meant to run once on first parse.
   * In this panel no card is ever parsed by the HTML parser, so without this the
   * drawn quick-buy control renders and then does nothing.
   */
  const ranScripts = new Set();

  /* Only these types are executable JavaScript. A product card also carries
     <script type="application/json"> data blocks, and re-running one of those as
     JavaScript throws "Unexpected token ':'". Those blocks are read in place by
     the card's own code and travel with the cloned card markup, so they are
     skipped here rather than copied (p1-grid's commit 0db8528a). */
  const JS_SCRIPT_TYPES = ['', 'text/javascript', 'application/javascript', 'module'];

  const runCardScripts = (doc) => {
    for (const source of doc.querySelectorAll('script')) {
      if (source.src) continue;
      const type = (source.getAttribute('type') || '').trim().toLowerCase();
      if (JS_SCRIPT_TYPES.indexOf(type) === -1) continue;
      const body = source.textContent || '';
      if (!body.trim() || ranScripts.has(body)) continue;
      ranScripts.add(body);
      const script = document.createElement('script');
      if (type === 'module') script.type = 'module';
      script.textContent = body;
      script.setAttribute('data-saba-nosto-card-script', '');
      document.head.appendChild(script);
    }
  };

  // The wishlist app fills the heart from its own state; this is how saved
  // hearts appear on cards that arrived after its last render.
  const refreshWishlist = () => {
    if (typeof window.sabaWishlistRefresh === 'function') window.sabaWishlistRefresh();
  };

  const component = () => ({
      accountId: '',
      searchUrl: '/search',
      /* the full query string for this market, resolved once in init() */
      query: QUERY,
      term: '',
      active: false,
      loading: false,
      failed: false,
      searched: false,
      keywords: [],
      hits: [],
      total: 0,
      hitCount: 0,
      redirect: null,
      /* set once cards are in the DOM, so the empty state cannot flash mid-request */
      cardCount: 0,

      /* resolved in init(): the cards container is x-ignore'd (Ben's ruling: Alpine never
         owns card internals) so $refs cannot reach it. */
      cardsEl: null,
      controller: null,
      timer: null,
      /* the term whose response is allowed to write to the DOM */
      currentTerm: '',
      startedAt: 0,

      get showEmpty() {
        return this.active && this.searched && !this.loading && !this.failed && this.cardCount === 0 && this.keywords.length === 0;
      },

      init() {
        this.accountId = this.$el.dataset.accountId || '';
        this.searchUrl = this.$el.dataset.searchUrl || '/search';
        /* Absent attributes mean this shopper's market takes no filter, and the
           query is then the one this file sent before the filter existed. */
        this.query = queryFor(marketClause(
          this.$el.dataset.marketFilterField || '',
          (this.$el.dataset.marketFilterValue || '').split(',').filter(Boolean)
        ));
        this.cardsEl = this.$el.querySelector('[data-nosto-cards]');

        const drawer = this.$el.closest('.drawer') || document.getElementById('search-drawer') || document;

        /* Delegated on the drawer root: the drawer re-slots its input node, so a direct
           node reference goes stale (JAG POC gotcha). Delegation survives replacement. */
        drawer.addEventListener('input', (event) => {
          const field = event.target.closest('.saba-search-input__field');
          if (!field) return;
          this.onInput(field.value);
        });

        /* The clear button fires `input` then a form `reset`; the native component used
           `reset` to return to the placeholder, so mirror that. */
        drawer.addEventListener('reset', () => this.reset());

        drawer.addEventListener('submit', (event) => {
          const form = event.target.closest('form');
          if (!form) return;
          const field = form.querySelector('.saba-search-input__field');
          if (!field || field.value.trim() === '') {
            event.preventDefault();
            return;
          }
          const submitted = field.value.trim();
          track((api) => api.recordSearchSubmit(submitted));
          /* Nosto keyword rules can redirect a term to a page instead of the results list. */
          if (this.redirect && this.term === field.value.trim()) {
            event.preventDefault();
            window.location.href = toPath(this.redirect);
          }
        });

        this.$el.addEventListener('click', (event) => this.onClick(event));
        // product-form dispatches variant:add only after cart/add.js succeeds
        document.addEventListener('variant:add', (event) => this.onAdd(event));

        /* Late script injection / late registration: if the shopper already typed before
           Alpine booted, pick the value up on init. */
        const existing = drawer.querySelector && drawer.querySelector('.saba-search-input__field');
        if (existing && existing.value) this.onInput(existing.value);
      },

      onClick(event) {
        const link = event.target.closest && event.target.closest('a[href]');
        if (!link) return;
        const href = link.getAttribute('href') || '';
        const keyword = link.closest('.predictive-search__suggestions')
          ? this.keywords.find((item) => item.url === href)
          : null;
        if (keyword) {
          try { sessionStorage.setItem('sabaNostoKeyword', keyword.raw); } catch (e) {}
          track((api) => api.recordSearchSubmit(keyword.raw));
          return;
        }
        if (href.indexOf(this.searchUrl + '?q=') === 0) {
          const term = this.term;
          track((api) => api.recordSearchSubmit(term));
          return;
        }
        const handle = handleOf(href);
        const hit = handle && this.hits.find((item) => handleOf(item.url) === handle);
        if (hit) track((api) => api.recordSearchClick('autocomplete', { productId: hit.productId, url: new URL(toPath(hit.url), window.location.origin).href }));
      },

      onAdd(event) {
        const handle = addedHandle(event, this.$el);
        const hit = handle && this.hits.find((item) => handleOf(item.url) === handle);
        if (hit) track((api) => api.recordSearchAddToCart('autocomplete', { productId: hit.productId, url: new URL(toPath(hit.url), window.location.origin).href }));
      },

      onInput(value) {
        const term = (value || '').trim();
        this.term = term;

        if (term.length < MIN_CHARS) {
          this.reset();
          return;
        }

        this.active = true;
        clearTimeout(this.timer);
        this.timer = setTimeout(() => this.run(term), DEBOUNCE_MS);
      },

      reset() {
        clearTimeout(this.timer);
        if (this.controller) this.controller.abort();
        this.controller = null;
        this.currentTerm = '';
        this.active = false;
        this.loading = false;
        this.failed = false;
        this.searched = false;
        this.keywords = [];
        this.hits = [];
        this.total = 0;
        this.hitCount = 0;
        this.cardCount = 0;
        this.redirect = null;
        if (this.cardsEl) this.cardsEl.replaceChildren();
      },

      async run(term) {
        if (this.controller) this.controller.abort();
        this.controller = new AbortController();
        const signal = this.controller.signal;
        this.currentTerm = term;
        this.loading = true;
        this.failed = false;
        this.startedAt = performance.now();
        /* what actually went on the wire, so the market filter is measurable */
        window.__sabaNostoAcLastQuery = this.query;

        try {
          const response = await fetch(ENDPOINT, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              query: this.query,
              variables: { accountId: this.accountId, q: term, kw: KEYWORD_SIZE, ps: PRODUCT_SIZE },
            }),
            signal,
          });
          /*
           * Decide on the PAYLOAD, never on the status or on `errors`.
           *
           * Nosto returns SOFT errors alongside completely valid data - a pin rule
           * plus an explicit sort answers 200 with every hit present and
           * errors: [{ message: "removing parameter from search, reason: pins
           * provided with sort" }] - and "some services are unavailable" also
           * arrives with a usable body. The status is not a reliable signal either
           * way: the same account answers 500 while still returning a parseable
           * body. So always parse, log any soft message, and ask the body only
           * whether products or keywords came back.
           */
          const payload = await response.json().catch(() => null);

          const softErrors = (payload && payload.errors) || [];
          if (softErrors.length) {
            // logged, never fatal - see above
            console.warn('[saba-nosto-autocomplete] Nosto reported:', softErrors.map((e) => e && e.message).join('; '));
          }

          const search = (payload && payload.data && payload.data.search) || null;
          /*
           * products null/missing AND keywords null/missing is the real failure.
           * An empty-but-present products ({ total: 0, hits: [] }) is a legitimate
           * no-results answer and must reach the empty state, not the error state.
           */
          if (!search || (!search.products && !search.keywords)) {
            throw new Error('no products or keywords in response');
          }

          if (this.currentTerm !== term) return;

          const hits = (search.products && search.products.hits) || [];

          this.hits = hits;
          this.redirect = search.redirect || null;
          this.total = (search.products && search.products.total) || 0;
          this.hitCount = hits.length;
          this.keywords = (((search.keywords || {}).hits) || []).map((hit) => ({
            keyword: sentenceCase(hit.keyword),
            raw: hit.keyword,
            redirected: !!hit._redirect,
            url: hit._redirect
              ? toPath(hit._redirect)
              : this.searchUrl + '?q=' + encodeURIComponent(hit.keyword),
          }));
          this.searched = true;
          track((api) => api.recordSearch('autocomplete', { query: term, products: { size: PRODUCT_SIZE } }, search));

          await this.renderCards(hits, term, signal);
        } catch (error) {
          if (error.name === 'AbortError' || this.currentTerm !== term) return;
          this.failed = true;
          this.searched = true;
          this.keywords = [];
          this.hitCount = 0;
          this.cardCount = 0;
          if (this.cardsEl) this.cardsEl.replaceChildren();
        } finally {
          if (this.currentTerm === term) this.loading = false;
        }
      },

      /**
       * One Section Rendering API request per hit against
       * sections/saba-nosto-drawer-card.liquid, so the cards are the theme's own
       * Liquid product-card markup injected as-is. Handles already in the cache
       * cost no request, which is what makes "dr" -> "dre" cheap.
       */
      async renderCards(hits, term, signal) {
        const handles = [];
        for (const hit of hits) {
          const handle = handleOf(hit.url);
          if (handle && !handles.includes(handle)) handles.push(handle);
        }

        if (!handles.length) {
          this.cardCount = 0;
          if (this.cardsEl) this.cardsEl.replaceChildren();
          return;
        }

        const wanted = handles.filter((handle) => !cardCache.has(handle));

        await Promise.all(
          wanted.map((handle) =>
            fetch(root() + 'products/' + encodeURIComponent(handle) + '?section_id=' + CARD_SECTION, { signal })
              .then((response) => (response.ok ? response.text() : ''))
              .then((html) => {
                if (!html) return;
                const doc = new DOMParser().parseFromString(html, 'text/html');
                // register the card's delegated behaviour once (see runCardScripts)
                runCardScripts(doc);
                const card = doc.querySelector('.product-card');
                /* The Section Rendering API answers 200 with a blank placeholder card
                   (empty handle, theme placeholder price) for handles that no longer
                   resolve to a live product, so response.ok is not enough. */
                if (!card || !card.getAttribute('handle')) return;
                /* Everything the card snippet renders BESIDE the card - today the
                   per-card wishlist drawer - is kept, not dropped. */
                const extras = [...doc.body.children]
                  .filter((el) => el !== card && el.tagName !== 'SCRIPT')
                  .map((el) => el.outerHTML);
                cachePut(handle, { card: card.outerHTML, extras: extras });
              })
              .catch(() => { /* a failed card just leaves that handle uncached */ })
          )
        );

        /* A slower batch for an earlier term must never overwrite the current one. */
        if (this.currentTerm !== term) return;

        const fragment = document.createDocumentFragment();
        let rendered = 0;
        for (const handle of handles) {
          const entry = cardFromCache(handle);
          if (!entry) continue;
          fragment.appendChild(entry.card);
          injectExtras(entry.extras);
          rendered += 1;
        }

        this.cardCount = rendered;
        if (this.cardsEl) this.cardsEl.replaceChildren(fragment);
        refreshWishlist();

        window.__sabaNostoAcTiming = {
          term,
          cards: rendered,
          requested: handles.length,
          fetched: wanted.length,
          fromCache: handles.length - wanted.length,
          msToFirstCard: Math.round(performance.now() - this.startedAt),
        };
      },
  });

  /**
   * Register whichever loads first, Alpine or this file. The loader snippet inserts
   * Alpine dynamically, which ignores `defer` and starts Alpine in a microtask on
   * arrival, so an alpine:init-only registration fails intermittently - and on a page
   * where another Nosto section has already booted Alpine, alpine:init has fired before
   * this file runs and would never fire again.
   */
  const register = () => {
    window.Alpine.data('sabaNostoAutocomplete', component);

    /* Called late, register() reads the DOM, so it must not run against a
       document still being parsed (crew rule: Alpine store registration). */
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', adoptExistingPanel, { once: true });
      return;
    }
    adoptExistingPanel();
  };

  const adoptExistingPanel = () => {
    /* Alpine.data() after Alpine.start() does not retroactively initialise an element
       Alpine has already walked, so initialise the panel here when we were late. */
    const el = document.getElementById('saba-nosto-autocomplete');
    if (el && !el._x_dataStack && typeof window.Alpine.initTree === 'function') {
      try {
        window.Alpine.initTree(el);
      } catch (e) {
        /* a failed re-init must not take the drawer down with it */
      }
    }
  };

  if (window.Alpine) register();
  else document.addEventListener('alpine:init', register, { once: true });
})();
